import { useQuery } from '@apollo/client';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import {
  UserBookingInvoicePrintableDocument,
  UserBookingInvoicePrintableQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import {
  Breadcrumb,
  Button,
  Col,
  Container,
  FloatingLabel,
  Form,
  Row,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import countries from '../../../dataObjects/countries';
import { BookingInvoiceInner } from './sections/BookingInvoice';

export function InvoiceTo({
  user,
}: {
  user: UserBookingInvoicePrintableQuery['booking']['user'];
}) {
  const { watch, register } = useForm({
    defaultValues: {
      name: `${user.givenName} ${user.familyName}`,
      line1: '',
      line2: '',
      line3: '',
      line4: '',
      line5: '',
    },
  });

  return (
    <>
      <Col xs={12} sm={6} className="d-print-none">
        <strong>To:</strong>
        <Form>
          <FloatingLabel controlId="name" label="Name" className="mb-3">
            <Form.Control {...register('name')} autoComplete="name" />
          </FloatingLabel>

          <FloatingLabel
            controlId="line1"
            label="Address Line 1"
            className="mb-3"
          >
            <Form.Control {...register('line1')} autoComplete="address-line1" />
          </FloatingLabel>

          <FloatingLabel
            controlId="line1"
            label="Address Line 2"
            className="mb-3"
          >
            <Form.Control {...register('line2')} autoComplete="address-line2" />
          </FloatingLabel>

          <FloatingLabel
            controlId="line1"
            label="Address Line 3"
            className="mb-3"
          >
            <Form.Control {...register('line3')} autoComplete="address-line3" />
          </FloatingLabel>

          <FloatingLabel
            controlId="line1"
            label="Address Line 4"
            className="mb-3"
          >
            <Form.Control {...register('line4')} autoComplete="postal-code" />
          </FloatingLabel>

          <FloatingLabel controlId="line1" label="Country" className="mb-3">
            <Form.Control {...register('line5')} autoComplete="country-name" />
          </FloatingLabel>
        </Form>
      </Col>

      <Col xs={6} className="d-none d-print-block">
        <strong>To:</strong>
        <p>
          {watch('name')}
          {watch('line1') !== '' && (
            <>
              <br />
              {watch('line1')}
            </>
          )}
          {watch('line2') !== '' && (
            <>
              <br />
              {watch('line2')}
            </>
          )}
          {watch('line3') !== '' && (
            <>
              <br />
              {watch('line3')}
            </>
          )}{' '}
          {watch('line4') !== '' && (
            <>
              <br />
              {watch('line4')}
            </>
          )}{' '}
          {watch('line5') !== '' && (
            <>
              <br />
              {watch('line5')}
            </>
          )}
        </p>
      </Col>
    </>
  );
}

export default function InvoicePrintable({ query }) {
  const { data, loading, error } = useQuery(
    UserBookingInvoicePrintableDocument,
    {
      variables: { id: query.bookingId },
      fetchPolicy: 'cache-and-network',
    },
  );

  // Handle loading/errors
  if (!data && loading) return <LoadingPage />;
  if (!data && error) return <ErrorPage error={error} />;

  const booking = data?.booking;
  const { host } = booking.trip;
  const hostAddress = host.businessAddress!;

  return (
    <Container className="mt-4">
      <Breadcrumb
        listProps={{ className: 'ps-0 justify-content-start' }}
        className="d-print-none"
      >
        <Link href="/" passHref legacyBehavior>
          <Breadcrumb.Item>Home</Breadcrumb.Item>
        </Link>
        <Link href={`/me/${booking.user.id}`} passHref legacyBehavior>
          <Breadcrumb.Item>Your dashboard</Breadcrumb.Item>
        </Link>
        <Link
          href={`/me/${booking.user.id}/bookings/${booking.id}`}
          passHref
          legacyBehavior
        >
          <Breadcrumb.Item>Booking {booking.id}</Breadcrumb.Item>
        </Link>
        <Breadcrumb.Item active>Invoice</Breadcrumb.Item>
      </Breadcrumb>
      <h1 className="d-none d-print-block">Invoice</h1>
      <h2 className="d-print-none h1">Create your printable invoice</h2>
      <p className="mb-0">
        <strong>Booking:</strong> {booking.id}
      </p>
      <p>
        <strong>Date:</strong>{' '}
        {DateTime.fromISO(booking.created, { zone: 'utc' }).toLocaleString()}
      </p>

      <Row>
        <InvoiceTo user={booking.user} />

        <Col>
          <strong>From:</strong>
          <p>
            {host.legalName}
            {hostAddress.addressLine1 && (
              <>
                <br />
                {hostAddress.addressLine1}
              </>
            )}
            {hostAddress.addressLine2 && (
              <>
                <br />
                {hostAddress.addressLine2}
              </>
            )}
            {hostAddress.postalCode && (
              <>
                <br />
                {hostAddress.postalCode}
              </>
            )}
            {hostAddress.sortingCode && (
              <>
                <br />
                {hostAddress.sortingCode}
              </>
            )}
            {hostAddress.dependentLocality && (
              <>
                <br />
                {hostAddress.dependentLocality}
              </>
            )}
            {hostAddress.locality && (
              <>
                <br />
                {hostAddress.locality}
              </>
            )}
            {hostAddress.administrativeArea && (
              <>
                <br />
                {hostAddress.administrativeArea}
              </>
            )}
            <br />
            {countries[hostAddress.country]}
          </p>
        </Col>
      </Row>

      <Row className="py-4">
        <Col>
          <BookingInvoiceInner booking={booking} hideAdminInvoice />
        </Col>
      </Row>

      <div className="d-grid gap-2 d-print-none">
        <Button onClick={() => window.print()} variant="primary" size="lg">
          Print
        </Button>
      </div>
    </Container>
  );
}
